import Api from '@/api'
import { rejectWithResponse, unwrap } from '@/utils/storeHelpers'

/**
 * Weekly timesheets tradies sent to an external supervisor, as the company
 * admin sees them: the list, one sheet with its trail, and the actions that
 * move it on (finalise, approve on the supervisor's behalf, resend the link,
 * amend hours). Nothing is kept in state: the External Approvals tab owns the
 * list and re-reads it after each action.
 */
export default {
  namespaced: true,

  actions: {
    fetchExternalTimesheets() {
      return rejectWithResponse(async () => {
        const res = await Api.get('external-timesheets')
        const data = unwrap(res)
        return Array.isArray(data) ? data : []
      })
    },

    // The listed sheets with their days, for the CSV/PDF export.
    fetchExportSheets() {
      return rejectWithResponse(async () => {
        const res = await Api.get('external-timesheets/export')
        const data = unwrap(res)
        return Array.isArray(data) ? data : []
      })
    },

    // Weeks a tradie worked and never sent, a week after they ended.
    fetchUnsentWeeks() {
      return rejectWithResponse(async () => {
        const res = await Api.get('external-timesheets/unsent')
        const data = unwrap(res)
        return Array.isArray(data) ? data : []
      })
    },

    fetchExternalTimesheet(ctx, { id }) {
      return rejectWithResponse(async () => {
        const res = await Api.get(`external-timesheets/${id}`)
        return unwrap(res)
      })
    },

    finaliseExternalTimesheet(ctx, { id }) {
      return rejectWithResponse(async () => {
        const res = await Api.post(`external-timesheets/${id}/finalise`)
        return unwrap(res)
      })
    },

    // One request for many; the API reports { finalised: [ids], failed: [{id, message}] }.
    finaliseManyExternalTimesheets(ctx, { ids }) {
      return rejectWithResponse(async () => {
        const res = await Api.post('external-timesheets/finalise', { ids })
        return res.data
      })
    },

    unfinaliseExternalTimesheet(ctx, { id }) {
      return rejectWithResponse(async () => {
        const res = await Api.post(`external-timesheets/${id}/unfinalise`)
        return unwrap(res)
      })
    },

    approveExternalTimesheetOnBehalf(ctx, { id, reason }) {
      return rejectWithResponse(async () => {
        const res = await Api.post(
          `external-timesheets/${id}/approve-on-behalf`,
          { reason },
        )
        return unwrap(res)
      })
    },

    resendExternalTimesheetLink(ctx, { id }) {
      return rejectWithResponse(async () => {
        const res = await Api.post(`external-timesheets/${id}/resend-link`)
        return unwrap(res)
      })
    },

    amendExternalTimesheet(ctx, { id, reason, days }) {
      return rejectWithResponse(async () => {
        const res = await Api.put(`external-timesheets/${id}/amend`, {
          reason,
          days,
        })
        return unwrap(res)
      })
    },
  },
}
